import React, { useState } from 'react';
import { CalendarDays, Loader2, Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { DateBlocksEditor } from './DateBlocksEditor';

const fmt = (iso) => new Date(iso).toLocaleDateString('de-DE');
const KINDS = [['all', 'Alle offenen Briefe'], ['standard', 'Nur Erstansprache-Briefe'], ['breakup', 'Nur Break-up-Briefe']];

// One letter date for every open letter in the queue — independent of series (Talu, 26.09.2026);
// or one date per block of 50 first letters for the postal batches (Talu, 10.10.2026).
export const QueueDateBar = ({ jobs, onChanged }) => {
  const [kind, setKind] = useState('all');
  const [busy, setBusy] = useState(false);
  const [blocksOpen, setBlocksOpen] = useState(false);
  const open = jobs.filter(j => (j.doc_type || 'letter') === 'letter' && ['hold', 'queued', 'error'].includes(j.status));
  if (!open.length) return null;
  const perLetter = {};
  open.forEach(j => { perLetter[j.letter_id || j.id] = j.date_override || ''; });
  const letters = Object.keys(perLetter).length;
  const counts = Object.values(perLetter).reduce((acc, d) => { acc[d] = (acc[d] || 0) + 1; return acc; }, {});
  const dates = Object.keys(counts);
  const current = dates.length === 1 ? dates[0] : '';

  const save = async (val) => {
    setBusy(true);
    try {
      const r = await api.setPrintQueueDate(val || null, kind);
      const n = r.data.letters;
      const skipped = r.data.skipped ? ` · ${r.data.skipped} übersprungen (andere Brief-Art)` : '';
      toast.success((val ? `Datum ${fmt(val)} für ${n} Brief${n !== 1 ? 'e' : ''} gesetzt — neu gerendert` : `${n} Brief${n !== 1 ? 'e' : ''} zurück auf das heutige Datum`) + skipped, { duration: 6000 });
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Datum konnte nicht geändert werden'); }
    finally { setBusy(false); }
  };

  return (
    <>
    <div className="px-4 py-2 border-b border-border flex items-center gap-2 flex-wrap bg-sky-500/5" data-testid="queue-date-bar">
      <CalendarDays className="w-3.5 h-3.5 text-sky-300 shrink-0" />
      <span className="text-[11px] text-foreground font-medium">Brief-Datum für alle auf einmal</span>
      <span className="text-[10px] text-muted-foreground font-mono" data-testid="queue-date-count">{letters} offene Brief{letters !== 1 ? 'e' : ''}</span>
      <span className="flex items-center gap-1 flex-wrap" data-testid="queue-date-summary">
        {dates.sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b))).map(d => (
          <span key={d || 'today'} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${d ? 'border-sky-500/40 bg-sky-500/10 text-sky-200' : 'border-border text-muted-foreground'}`}>
            {d ? fmt(d) : 'heute'} × {counts[d]}
          </span>
        ))}
      </span>
      <select value={kind} onChange={e => setKind(e.target.value)} disabled={busy} data-testid="queue-date-kind"
        className="h-6 text-[10px] rounded-sm bg-secondary/40 border border-border px-1 text-foreground focus:outline-none">
        {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <input type="date" value={dates.length === 1 ? current : ''} disabled={busy} data-testid="queue-date-input"
        onChange={e => e.target.value && save(e.target.value)}
        className="h-6 text-[10px] rounded-sm bg-secondary/40 border border-sky-500/40 px-1 text-foreground focus:outline-none" />
      {(current || dates.length > 1) && (
        <button onClick={() => save(null)} disabled={busy} data-testid="queue-date-reset"
          className="text-[10px] px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground hover:text-foreground" title="Alle zurück auf das heutige Datum">heute</button>
      )}
      {busy && <Loader2 className="w-3 h-3 animate-spin text-sky-300" />}
      <button onClick={() => setBlocksOpen(v => !v)} data-testid="queue-date-blocks-toggle"
        title="Post-Chargen à 50: ein Datum je Block von 50 Erstansprache-Briefen (in Schreibreihenfolge)"
        className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm border transition-colors ${blocksOpen ? 'border-sky-500/50 bg-sky-500/15 text-sky-200' : 'border-border text-muted-foreground hover:text-foreground'}`}>
        <Layers className="w-3 h-3" /> Datum in 50er-Blöcken {blocksOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      <span className="text-[10px] text-muted-foreground/70 ml-auto hidden xl:inline">Gilt für alle noch nicht geschriebenen Briefe — die Briefe werden mit dem Datum neu gerendert.</span>
    </div>
    {blocksOpen && <DateBlocksEditor kind="standard" onChanged={onChanged} />}
    </>
  );
};
